import * as React from "react";
import { Bold, HelpCircle, Italic, Link as LinkIcon, List } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RichText } from "@/lib/richText";
import { cn } from "@/lib/utils";

/**
 * A small markdown editor over the app's homegrown {@link RichText} subset —
 * **bold**, *italic*, [links](https://…), and `- ` bullet lists. A toolbar
 * inserts the syntax around the selection, and a Write/Preview switch renders
 * exactly what a viewer will see (same component, so no drift). Deliberately no
 * WYSIWYG dependency: the stored value stays plain markdown text, which the
 * public page already renders XSS-safe.
 */
export function MarkdownEditor({
  id,
  value,
  onChange,
  maxLength,
  placeholder,
  fill,
  taRef,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  maxLength?: number;
  placeholder?: string;
  /** Start tall (~26rem) so the editor fills the space next to other content, while staying
   *  freely drag-resizable (the textarea keeps `resize-y`). NB: do NOT flex-fill the textarea —
   *  flex-grow overrides the native resize and the drag handle stops working. A caller can seed
   *  a precise starting height imperatively through {@link taRef} instead (see BookingPageBuilder). */
  fill?: boolean;
  /** Exposes the underlying <textarea> so a caller can seed/measure its height. */
  taRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
}) {
  const [tab, setTab] = React.useState<"write" | "preview">("write");
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  const pendingSel = React.useRef<[number, number] | null>(null);

  // Restore the selection after a toolbar edit re-renders the controlled value.
  React.useLayoutEffect(() => {
    if (pendingSel.current && ref.current) {
      const [s, e] = pendingSel.current;
      ref.current.focus();
      ref.current.setSelectionRange(s, e);
      pendingSel.current = null;
    }
  }, [value]);

  function commit(next: string, sel: [number, number]) {
    if (maxLength && next.length > maxLength) return;
    pendingSel.current = sel;
    onChange(next);
  }

  function wrap(before: string, after: string, ph: string) {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const inner = value.slice(start, end) || ph;
    const next = value.slice(0, start) + before + inner + after + value.slice(end);
    const from = start + before.length;
    commit(next, [from, from + inner.length]);
  }

  function bulletLines() {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const block = value.slice(lineStart, end) || "";
    const prefixed = (block || "item")
      .split("\n")
      .map((l) => (/^[-*] /.test(l) ? l : `- ${l}`))
      .join("\n");
    const next = value.slice(0, lineStart) + prefixed + value.slice(end);
    commit(next, [lineStart, lineStart + prefixed.length]);
  }

  const writing = tab === "write";

  return (
    <div className="overflow-hidden rounded-md border-2 border-black bg-background">
      <div className="flex items-center justify-between gap-2 border-b border-border/50 bg-muted/30 px-1.5 py-1">
        <div className="flex gap-0.5">
          <ToolBtn label="Bold" disabled={!writing} onClick={() => wrap("**", "**", "bold")}>
            <Bold className="h-3.5 w-3.5" />
          </ToolBtn>
          <ToolBtn label="Italic" disabled={!writing} onClick={() => wrap("*", "*", "italic")}>
            <Italic className="h-3.5 w-3.5" />
          </ToolBtn>
          <ToolBtn label="Link" disabled={!writing} onClick={() => wrap("[", "](https://)", "link text")}>
            <LinkIcon className="h-3.5 w-3.5" />
          </ToolBtn>
          <ToolBtn label="Bullet list" disabled={!writing} onClick={bulletLines}>
            <List className="h-3.5 w-3.5" />
          </ToolBtn>
          <FormattingHelp />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="hidden text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:inline">
            Markdown
          </span>
          <div className="flex gap-0.5">
            <TabBtn active={writing} onClick={() => setTab("write")}>
              Write
            </TabBtn>
            <TabBtn active={!writing} onClick={() => setTab("preview")}>
              Preview
            </TabBtn>
          </div>
        </div>
      </div>

      {writing ? (
        <textarea
          id={id}
          ref={(el) => {
            ref.current = el;
            if (taRef) taRef.current = el;
          }}
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "block min-h-[120px] w-full resize-y bg-transparent px-3 py-2 text-sm focus:outline-none",
            fill && "h-[26rem]",
          )}
        />
      ) : value.trim() ? (
        <RichText
          text={value}
          className={cn("min-h-[120px] px-3 py-2 text-sm leading-relaxed", fill && "min-h-[26rem]")}
        />
      ) : (
        <p
          className={cn(
            "min-h-[120px] px-3 py-2 text-sm italic text-muted-foreground",
            fill && "min-h-[26rem]",
          )}
        >
          Nothing to preview yet.
        </p>
      )}

      {maxLength ? (
        <div className="flex justify-end border-t border-border/50 bg-muted/20 px-3 py-1">
          <span
            title="Counts the characters you type (markdown), not the formatted preview."
            className={cn(
              "text-[11px] tabular-nums",
              value.length >= maxLength
                ? "font-semibold text-rose-600 dark:text-rose-400"
                : value.length >= maxLength * 0.9
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-muted-foreground",
            )}
          >
            {value.length} / {maxLength}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** A "?" in the toolbar that opens a tiny Markdown cheatsheet, so people who
 *  don't know the syntax can still format — the toolbar buttons do the same,
 *  but the cheatsheet makes the whole grammar (and that it's Markdown) explicit. */
function FormattingHelp() {
  const rows: [string, string][] = [
    ["**bold**", "bold"],
    ["*italic*", "italic"],
    ["[text](https://…)", "a link"],
    ["- item", "a bullet list"],
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Formatting help"
          title="Formatting help"
          className="grid h-7 w-7 place-items-center rounded text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64">
        <p className="mb-2 text-xs font-semibold">This field supports Markdown</p>
        <table className="w-full text-xs">
          <tbody>
            {rows.map(([syntax, result]) => (
              <tr key={syntax}>
                <td className="py-1 pr-3 align-top">
                  <code className="rounded bg-muted px-1 py-0.5">{syntax}</code>
                </td>
                <td className="py-1 align-top text-muted-foreground">→ {result}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Use the buttons above, or type the syntax. Switch to <strong>Preview</strong> to see
          exactly what people will read.
        </p>
      </PopoverContent>
    </Popover>
  );
}

function ToolBtn({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="grid h-7 w-7 place-items-center rounded transition hover:bg-muted disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide transition",
        active ? "bg-teal-300 text-teal-950" : "text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
